import { createSelector } from 'reselect';
import { get, map, unescape } from 'lodash';

import { byIdSelector } from '../../common/selectors/claimed-domains-with-counts-selectors';
import { domainsByIdSelector } from '../../common/selectors/domains-selectors';
import { domainGroupsByIdSelector } from '../../common/selectors/domain-groups-selectors';
import { flatAggregateResultsByDomainSelector } from '../../common/selectors/aggregate-results-selectors';
import { rightPaneTitleSelector } from '../../common/selectors/right-pane-selectors';

import { DMARC_NONE } from '../../common/constants/dmarc-policy-values';

const mapDomainData = ( row ) => {
    const authorizedMailVolume = row.authorizedMailVolume;
    const unauthorizedMailVolume = row.unauthorizedMailVolume;

    const calculateRate = ( value, isAuthorized = true ) => {
        const mailVolume = isAuthorized ? authorizedMailVolume : unauthorizedMailVolume;
        const calculatedRate = mailVolume > 0 ? ( value / mailVolume ) * 100 : 0;

        return calculatedRate.toFixed( 2 );
    };

    return {
        actionsCount: row.actionDetails ? row.actionDetails.length : 0,
        authorizedMailVolume,
        dkimAlignRate: calculateRate( row.dkimAlignCount ),
        dkimPassAlignRate: calculateRate( row.dkimPassAlignCount ),
        dkimPassRate: calculateRate( row.dkimPassCount ),
        dmarcDoublePassRate: calculateRate( row.dmarcDoublePassCount ),
        dmarcPassRate: calculateRate( row.dmarcPassCount ),
        dmarcPolicy: get( row, 'domainDetails.dmarc.policy', DMARC_NONE ),
        domainCount: row.domainDetails.orgDomain ? 1 : 0,
        domainDetails: row.domainDetails,
        domainName: row.domainName,
        domainType: get( row, 'domainDetails.domainType', null ),
        orgDomain: row.domainDetails.orgDomain,
        percentBlocked: calculateRate( row.blockedMailCount ),
        spfAlignRate: calculateRate( row.spfAlignCount ),
        spfPassAlignRate: calculateRate( row.spfPassAlignCount ),
        spfPassRate: calculateRate( row.spfPassCount ),
        unauthorizedBlockRate: calculateRate( row.unauthorizedBlockedMailCount, false ),
        unauthorizedDMARCPassRate: calculateRate( row.unauthorizedDMARCPassCount, false ),
        unauthorizedMailVolume,
    };
};

export const domainsDetailsPaneSelector = createSelector(
    flatAggregateResultsByDomainSelector,
    domainsByIdSelector,
    rightPaneTitleSelector,
    ( aggResultsByDomain, domainDetailByDomain, rightPaneDomainName ) => {
        const domainAggResults = aggResultsByDomain.find( aggResult => aggResult.domainName === rightPaneDomainName );

        return mapDomainData( { ...domainAggResults, domainDetails: domainDetailByDomain[rightPaneDomainName] } );
    }
);

export const claimedDomainsDetailsPaneSelector = createSelector(
    byIdSelector,
    rightPaneTitleSelector,
    domainGroupsByIdSelector,
    ( domainsWithCountsById, rightPaneDomainName, allDomainGroupIds ) => {
        const claimedDomainDetails = domainsWithCountsById[rightPaneDomainName];
        const domainGroupNames = map( claimedDomainDetails.domainGroups, ( groupId ) => unescape( get( allDomainGroupIds[groupId], 'name', '' ) ) );

        return { ...claimedDomainDetails, domainGroups: domainGroupNames };
    } );
